const TERMINALS = ['start', 'end'];

/** Hit margin in page px (at zoom 1) so dropping a free end onto a pin is easier. */
const BIND_MARGIN_PX = 12;

function clamp01(n) {
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
}

function readArrowBindings(editor, arrow) {
  const list = editor.getBindingsFromShape?.(arrow.id ?? arrow, 'arrow') || [];
  return {
    start: list.find(b => b?.props?.terminal === 'start'),
    end: list.find(b => b?.props?.terminal === 'end'),
  };
}

function normalizedAnchorAtPagePoint(editor, target, pagePoint) {
  const geometry = editor.getShapeGeometry?.(target);
  const bounds = geometry?.bounds;
  if (!bounds || bounds.w < 1 || bounds.h < 1) {
    return { x: 0.5, y: 0.5 };
  }
  const local = editor.getPointInShapeSpace?.(target, pagePoint) || { x: 0, y: 0 };
  return {
    x: clamp01((local.x - bounds.x) / bounds.w),
    y: clamp01((local.y - bounds.y) / bounds.h),
  };
}

function pagePointForTerminal(editor, arrow, terminal) {
  const transform = editor.getShapePageTransform?.(arrow.id);
  const local = arrow?.props?.[terminal];
  if (!transform || !local) return null;
  return transform.applyToPoint(local);
}

function findBindTarget(editor, arrow, pagePoint) {
  if (!pagePoint || !editor?.getShapeAtPoint) return null;
  const zoom = editor.getZoomLevel?.() || 1;
  const margin = BIND_MARGIN_PX / zoom;

  return editor.getShapeAtPoint(pagePoint, {
    hitInside: true,
    hitFrameInside: true,
    margin,
    filter: (targetShape) => {
      if (!targetShape || targetShape.isLocked) return false;
      if (targetShape.id === arrow.id) return false;
      return Boolean(
        editor.canBindShapes?.({
          fromShape: arrow,
          toShape: targetShape,
          binding: 'arrow',
        })
      );
    },
  });
}

function upsertArrowTerminalBinding(editor, arrow, terminal, target, pagePoint) {
  const bindings = readArrowBindings(editor, arrow);
  const existing = bindings?.[terminal];
  const normalizedAnchor = normalizedAnchorAtPagePoint(editor, target, pagePoint);
  const props = {
    terminal,
    normalizedAnchor,
    isPrecise: true,
    isExact: false,
    snap: 'none',
  };

  if (existing) {
    editor.updateBinding?.({
      ...existing,
      toId: target.id,
      props: {
        ...existing.props,
        ...props,
      },
    });
    return;
  }

  editor.createBinding?.({
    type: 'arrow',
    fromId: arrow.id,
    toId: target.id,
    props,
  });
}

function removeArrowTerminalBinding(editor, arrow, terminal) {
  const bindings = readArrowBindings(editor, arrow);
  const existing = bindings?.[terminal];
  if (!existing) return;
  editor.deleteBindings?.([existing]);
}

/**
 * Which connect-line ends were free when a translate began.
 * Stock tldraw only maintains bindings that already exist; we sync these.
 *
 * @param {import('tldraw').Editor} editor
 * @param {{ id: string, type?: string }} arrow
 */
export function readUnboundArrowTerminals(editor, arrow) {
  const bindings = readArrowBindings(editor, arrow);
  return {
    start: !bindings?.start,
    end: !bindings?.end,
  };
}

/**
 * While / after translating a connect line: bind free ends that sit on a
 * pin, note, frame, etc., and unbind if the free end leaves the target.
 *
 * @param {import('tldraw').Editor} editor
 * @param {string} arrowId
 * @param {{ start?: boolean, end?: boolean }} terminals  ends that started unbound
 */
export function syncUnboundArrowTerminalsOverTargets(editor, arrowId, terminals) {
  if (!editor || !arrowId || !terminals) return;

  const arrow = editor.getShape?.(arrowId);
  if (!arrow || arrow.type !== 'arrow') return;
  if (!terminals.start && !terminals.end) return;

  for (const terminal of TERMINALS) {
    if (!terminals[terminal]) continue;

    const pagePoint = pagePointForTerminal(editor, arrow, terminal);
    if (!pagePoint) continue;

    const target = findBindTarget(editor, arrow, pagePoint);
    if (!target) {
      removeArrowTerminalBinding(editor, arrow, terminal);
      continue;
    }

    upsertArrowTerminalBinding(editor, arrow, terminal, target, pagePoint);
  }

  // Avoid a zero-length connector when both ends bind to the same anchor.
  const next = readArrowBindings(editor, arrow);
  if (
    next.start
    && next.end
    && next.start.toId === next.end.toId
    && next.start.props?.normalizedAnchor?.x === next.end.props?.normalizedAnchor?.x
    && next.start.props?.normalizedAnchor?.y === next.end.props?.normalizedAnchor?.y
  ) {
    editor.updateBinding?.({
      ...next.end,
      props: {
        ...next.end.props,
        normalizedAnchor: {
          x: clamp01((next.end.props.normalizedAnchor?.x ?? 0.5) + 0.05),
          y: next.end.props.normalizedAnchor?.y ?? 0.5,
        },
      },
    });
  }
}
